/**
 * Export Crew Hours summary (sailor×seat + by-crew) as a two-sheet .xlsx.
 */
import { error as logError } from "./console";
import { CREW_HOUR_SEATS, type CrewHourFilter, type CrewHourSummary } from "./crewHours";

const HEADER_FILL = "FFEFEFEF";
const TOTAL_FILL = "FFE8EEF5";
const BORDER_COLOR = "FF808080";

function sanitizeSheetName(name: string): string {
  const cleaned = name.replace(/[\\/*?:[\]]/g, " ").trim();
  return (cleaned === "" ? "Crew Hours" : cleaned).slice(0, 31);
}

function sanitizeFileName(name: string): string {
  const cleaned = name.replace(/[\\/:*?"<>|]/g, " ").replace(/\s+/g, " ").trim();
  return cleaned === "" ? "Crew Hours" : cleaned;
}

function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

function roundHours(hours: number): number | null {
  if (!Number.isFinite(hours) || hours <= 0) return null;
  return Math.round(hours * 10) / 10;
}

function modeLabel(mode: CrewHourFilter["mode"]): string {
  if (mode === "racing") return "Racing";
  if (mode === "training") return "Training";
  return "All";
}

export interface CrewHoursExcelArgs {
  summary: CrewHourSummary;
  filter: CrewHourFilter;
  sourceName: string;
}

export async function exportCrewHoursExcel(
  args: CrewHoursExcelArgs
): Promise<{ success: boolean; message?: string }> {
  try {
    const { summary, filter, sourceName } = args;
    if (summary.rows.length === 0 && summary.byCrew.length === 0) {
      return { success: false, message: "No crew hours in this range to export." };
    }

    const ExcelJS = await import("exceljs");
    const workbook = new ExcelJS.Workbook();
    const thinBorder = {
      top: { style: "thin" as const, color: { argb: BORDER_COLOR } },
      left: { style: "thin" as const, color: { argb: BORDER_COLOR } },
      bottom: { style: "thin" as const, color: { argb: BORDER_COLOR } },
      right: { style: "thin" as const, color: { argb: BORDER_COLOR } },
    };
    const center = { horizontal: "center" as const, vertical: "middle" as const };
    const left = { horizontal: "left" as const, vertical: "middle" as const };

    const sailorHeaders = [
      "Sailor",
      ...CREW_HOUR_SEATS.map((seat) => seat.label),
      "Total",
    ];
    const sailorSheet = workbook.addWorksheet(sanitizeSheetName("By sailor"), {
      views: [{ showGridLines: false, state: "frozen", ySplit: 1 }],
    });
    for (let c = 1; c <= sailorHeaders.length; c++) {
      sailorSheet.getColumn(c).width = c === 1 ? 16 : 11;
      const cell = sailorSheet.getCell(1, c);
      cell.value = sailorHeaders[c - 1];
      cell.font = { bold: true, size: 10 };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: HEADER_FILL } };
      cell.alignment = c === 1 ? left : center;
      cell.border = thinBorder;
    }

    let rowIndex = 2;
    for (const row of summary.rows) {
      const values: Array<string | number | null> = [
        row.sailor,
        ...CREW_HOUR_SEATS.map((seat) => roundHours(row.hours[seat.key])),
        roundHours(row.total),
      ];
      for (let c = 1; c <= sailorHeaders.length; c++) {
        const cell = sailorSheet.getCell(rowIndex, c);
        const v = values[c - 1];
        if (v != null && v !== "") cell.value = v;
        cell.alignment = c === 1 ? left : center;
        cell.border = thinBorder;
      }
      rowIndex += 1;
    }

    if (summary.rows.length > 0) {
      const totals: Array<string | number | null> = [
        "Seat occupied",
        ...CREW_HOUR_SEATS.map((seat) => roundHours(summary.seatTotals[seat.key])),
        null,
      ];
      for (let c = 1; c <= sailorHeaders.length; c++) {
        const cell = sailorSheet.getCell(rowIndex, c);
        const v = totals[c - 1];
        if (v != null && v !== "") cell.value = v;
        cell.font = { bold: true, size: 10 };
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: TOTAL_FILL } };
        cell.alignment = c === 1 ? left : center;
        cell.border = thinBorder;
      }
    }

    const crewHeaders = ["Crew", "Intervals", "Hours"];
    const crewSheet = workbook.addWorksheet(sanitizeSheetName("By crew"), {
      views: [{ showGridLines: false, state: "frozen", ySplit: 1 }],
    });
    for (let c = 1; c <= crewHeaders.length; c++) {
      crewSheet.getColumn(c).width = c === 1 ? 14 : 12;
      const cell = crewSheet.getCell(1, c);
      cell.value = crewHeaders[c - 1];
      cell.font = { bold: true, size: 10 };
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: HEADER_FILL } };
      cell.alignment = c === 1 ? left : center;
      cell.border = thinBorder;
    }
    rowIndex = 2;
    for (const row of summary.byCrew) {
      const values: Array<string | number | null> = [
        row.code,
        row.intervals,
        roundHours(row.hours),
      ];
      for (let c = 1; c <= crewHeaders.length; c++) {
        const cell = crewSheet.getCell(rowIndex, c);
        const v = values[c - 1];
        if (v != null && v !== "") cell.value = v;
        cell.alignment = c === 1 ? left : center;
        cell.border = thinBorder;
      }
      rowIndex += 1;
    }

    const boat = sanitizeFileName(sourceName || "Boat");
    const mode = modeLabel(filter.mode);
    const start = filter.startDate.trim() || "start";
    const end = filter.endDate.trim() || "end";
    const fileName = `${boat} - CREW HOURS - ${mode} - ${start} to ${end}.xlsx`;

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    downloadBlob(blob, fileName);
    return { success: true };
  } catch (err) {
    logError("[crewHoursExcel] export failed", err);
    return {
      success: false,
      message: err instanceof Error ? err.message : "Failed to export Excel file",
    };
  }
}
